'use client';

import { motion } from 'framer-motion';
import { PanelLeftClose, PanelLeftOpen, UserCircle } from 'lucide-react';
import router from 'next/router';
import { signIn, useSession } from 'next-auth/react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { Icon } from '@/components/icon';
import { Image } from '@/components/image';
import { Link } from '@/components/link';
import { VisuallyHidden } from '@/components/visually-hidden';
import { useAppNavigation } from '@/hooks/use-app-navigation';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useSavedItems } from '@/hooks/use-saved-items';
import { useUserNotifications } from '@/hooks/use-user-notifications';
import { useWindowSize } from '@/hooks/use-window-size';
import { APP_NAV_SIZES, SAVED_ITEM_TYPE, USER_ACCESS_FEATURES } from '@/lib/constants';
import { FEATURE_FLAGS } from '@/lib/constants/feature-flags';
import { featuresState } from '@/state/features';
import { appNavState, mobileAppNavIsOpenState } from '@/state/navigation';
import { BREAKPOINTS_AS_NUMBERS } from '@/theme/constants/breakpoints';
import { canAccessFeature } from '@/utils/auth';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

import { AppNavigationClosed } from './closed';
import { ExtraNavButton, extraNavButtonStyles } from './extra-nav-button';
import { AppNavigationHelpPopover } from './help-popover';
import { AppNavigationNotificationsDrawer } from './notifications-drawer';
import { AppNavigationOpen } from './open';
import { UserPopover } from './user-popover';
import { baseVariants, navItemVariants } from './utils/transitions';

export interface AppNavigationProps {
  children: React.ReactNode;
  showAllItemsOverride?: boolean; // Only used in storybook and tests
}

const AppNavigation = ({ children, showAllItemsOverride }: AppNavigationProps) => {
  const { data: session } = useSession();
  const { width } = useWindowSize();
  const { navItems } = useAppNavigation({ session, showAllItemsOverride });
  const { savedItems, deleteSavedItem } = useSavedItems({
    types: [SAVED_ITEM_TYPE.ROUTE, SAVED_ITEM_TYPE.SCHEDULE, SAVED_ITEM_TYPE.MARKET_RATE, SAVED_ITEM_TYPE.VEHICLE],
  });

  const [appNav, setAppNav] = useRecoilState(appNavState);
  const [mobileNavIsOpen, setMobileNavIsOpen] = useRecoilState(mobileAppNavIsOpenState);
  const features = useRecoilValue(featuresState);
  const { isOpen, openAccordionItems } = appNav;

  const { variant: isEnabled, isLoading } = useFeatureFlag(FEATURE_FLAGS.USER_NOTIFICATIONS);
  const USER_NOTIFICATIONS_ENABLED = !isLoading && isEnabled === true;
  const USER_AUTH_ON = !!features.userAuthentication;
  const HAS_SUPPORT_ACCESS = canAccessFeature(USER_ACCESS_FEATURES.SUPPORT, session);

  useUserNotifications({ isEnabled: USER_NOTIFICATIONS_ENABLED });

  const isSmallScreen = width! < BREAKPOINTS_AS_NUMBERS.MD;

  const visibility = isOpen ? 'visible' : 'hidden';

  const handleToggleNav = (value: boolean) => {
    if (!value) {
      // if we are closing the menu, ensure we set the storedAccordionItems
      setAppNav((prev) => ({
        ...prev,
        isOpen: value,
        openAccordionItems: [],
        storedAccordionItems: prev.openAccordionItems,
      }));
    } else {
      setAppNav({ ...appNav, isOpen: value });
    }

    trackUser.event('Toggle App Navigation', { value: value ? 'Open' : 'Close' });
  };

  const handleToggleNotifications = () => {
    setAppNav((prev) => ({
      ...prev,
      isNotificationsOpen: !prev.isNotificationsOpen,
    }));
  };

  const handleOpenAccordionItems = (value: string[]) => {
    setAppNav((prev) => ({ ...prev, openAccordionItems: value }));

    trackUser.event('Open App Navigation Accordion Items', { value });
  };

  const handleNavigateTo = (url?: string) => {
    if (url) {
      router.push(url);
    }
  };

  const handleSignIn = () => {
    signIn();

    trackUser.event('Go to: Sign-in', {
      location: 'Nav',
    });
  };

  const handleAnimationEnd = () => {
    // Restore menu items from State
    if (isOpen && !openAccordionItems.length) {
      setAppNav((prev) => ({ ...prev, openAccordionItems: prev.storedAccordionItems }));
    }
  };

  const { toggleButton } = extraNavButtonStyles();
  const { base, logo, logoText, settings, bodyOverlay, content, inner, innerWrapper, scroller, transitionStrip } =
    styles({
      mobileNavIsOpen,
    });

  return (
    <div className={content()}>
      {USER_NOTIFICATIONS_ENABLED ? (
        <AppNavigationNotificationsDrawer
          offset={isOpen ? APP_NAV_SIZES.open : APP_NAV_SIZES.closed}
          isOpen={appNav.isNotificationsOpen}
          onOpenChange={handleToggleNotifications}
        />
      ) : null}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <span className={bodyOverlay()} onClick={() => setMobileNavIsOpen(false)}>
        <VisuallyHidden>Close Nav</VisuallyHidden>
      </span>
      <motion.div
        variants={baseVariants}
        animate={isOpen ? 'open' : 'closed'}
        initial={isOpen ? 'open' : 'closed'}
        className={base()}
        custom={isSmallScreen}
        onAnimationComplete={handleAnimationEnd}
      >
        <Link href="/" className={logo()}>
          <Icon name="logo-icon" size="lg" />
          <motion.span
            variants={navItemVariants}
            animate={isOpen ? 'visible' : 'hidden'}
            initial={isOpen ? 'visible' : 'hidden'}
            className={logoText()}
          >
            <Image src="/assets/icons/logo-text.svg" alt="Fluent Cargo" />
          </motion.span>
          <VisuallyHidden>Go to home page</VisuallyHidden>
        </Link>
        <div className={scroller()}>
          <AppNavigationOpen
            isOpen={isOpen}
            openAccordionItems={openAccordionItems}
            handleOpenAccordionItems={handleOpenAccordionItems}
            navItems={navItems()}
            handleOpenNotifications={handleToggleNotifications}
            handleNavigateTo={handleNavigateTo}
            isUserNotificationsEnabled={USER_NOTIFICATIONS_ENABLED}
            savedItems={savedItems}
            onDeleteSavedItem={deleteSavedItem}
          />
          <AppNavigationClosed
            isOpen={isOpen}
            navItems={navItems()}
            handleOpenNotifications={handleToggleNotifications}
            handleNavigateTo={handleNavigateTo}
            isUserNotificationsEnabled={USER_NOTIFICATIONS_ENABLED}
          />
        </div>
        <span className={transitionStrip()} />
        <div className={settings()}>
          <UserPopover isOpen={isOpen} session={session} visibility={visibility} userAuthOn={USER_AUTH_ON} />

          {!session && (
            <ExtraNavButton
              onClick={() => handleSignIn()}
              buttonText="Sign in"
              icon={<UserCircle size={18} />}
              visibility={visibility}
            />
          )}

          {HAS_SUPPORT_ACCESS && <AppNavigationHelpPopover visibility={visibility} />}

          <ExtraNavButton
            onClick={() => handleToggleNav(!isOpen)}
            buttonText="Collapse menu"
            icon={isOpen ? <PanelLeftClose size={18} /> : <PanelLeftOpen size={18} />}
            visibility={visibility}
            className={toggleButton()}
          />
        </div>
      </motion.div>
      <div className={inner()}>
        <div className={innerWrapper()}>{children}</div>
      </div>
    </div>
  );
};

export const styles = tv({
  slots: {
    base: 'relative z-8 h-[calc(100%-var(--app-mobile-header-height)+3px)] shrink-0 bg-blue-800 p-2 pt-4 text-white max-md:fixed max-md:bottom-0 max-md:top-[61px] max-md:w-1/2 max-md:transition-all max-md:duration-300 md:h-lvh md:max-w-48 md:overflow-hidden',
    content: 'relative flex h-dvh bg-grey-100 max-md:mt-16 max-md:h-[calc(100dvh-var(--app-mobile-header-height))]',
    logo: 'mb-4 flex translate-x-1 items-end max-md:hidden',
    logoText: 'ml-2 !translate-y-0.5',
    settings: 'absolute bottom-3 left-3 right-3 z-3 border-t border-white/10 md:max-w-48 md:border-0',
    bodyOverlay: 'body-overlay',
    inner: 'h-full w-full transition-all duration-300',
    innerWrapper:
      'absolute bottom-0 left-0 right-0 top-0 grid grid-rows-[var(--app-breadcrumbs-height)_1fr] overflow-hidden md:grid-rows-[var(--app-breadcrumbs-height-md)_1fr]',
    scroller: 'absolute bottom-[70px] left-0 right-0 top-0 overflow-auto overflow-x-hidden md:bottom-[135px] md:top-16',
    transitionStrip:
      'absolute bottom-[70px] left-0 right-0 z-2 h-4 bg-gradient-to-t from-blue-800 to-transparent md:bottom-[135px]',
  },
  variants: {
    mobileNavIsOpen: {
      false: {
        base: 'max-md:-translate-x-[var(--app-nav-width)]',
        inner: 'translate-x-0',
      },
      true: {
        base: 'min-w-48 max-w-80 max-md:translate-x-0',
        content: 'overflow-hidden',
        bodyOverlay: '!pointer-events-auto !opacity-100',
        inner: 'max-md:translate-x-8 max-md:blur-[2px]',
      },
    },
  },
});

export { AppNavigation };
